import React, { useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Building2, Users, Clock, Loader2, AlertTriangle, FileText, CheckCircle2,
  ClipboardList, Radar, Inbox, Layers, Trophy, HeartPulse, Flag, GraduationCap,
  Rocket, ShieldAlert, Hourglass,
} from 'lucide-react';
import { adminApi } from '@/lib/adminApi';

/**
 * Role-aware Suite Ops layer (Founder UAT — Suite Console batch).
 * HM Admin: cross-client/cross-advisor supervision. HM Advisor: own book only.
 * All numbers come from /admin/suites/{code}/console-summary (real data, book-scoped
 * server-side — the KPI value and its drill-down share the same authorization scope).
 */

const KPI_SETS = {
  talentcheck: [
    { k: 'active_clients', icon: Building2, tone: 'primary' },
    { k: 'pending', icon: Clock, tone: 'warning', to: (s) => `/admin/evaluations?suite=${s}` },
    { k: 'in_progress', icon: Loader2, tone: 'info', to: (s) => `/admin/evaluations?suite=${s}` },
    { k: 'overdue', icon: AlertTriangle, tone: 'destructive', to: (s) => `/admin/evaluations?suite=${s}` },
    { k: 'reports_pending', icon: FileText, tone: 'warning', to: () => '/admin/reports' },
    { k: 'reports_released', icon: CheckCircle2, tone: 'success', to: () => '/admin/reports' },
  ],
  talentmap: [
    { k: 'active_clients', icon: Building2, tone: 'primary' },
    { k: 'pending', icon: Clock, tone: 'warning', to: (s) => `/admin/evaluations?suite=${s}` },
    { k: 'in_progress', icon: Loader2, tone: 'info', to: (s) => `/admin/evaluations?suite=${s}` },
    { k: 'submitted', icon: ClipboardList, tone: 'success', to: (s) => `/admin/evaluations?suite=${s}&status=submitted` },
    { k: 'overdue', icon: AlertTriangle, tone: 'destructive', to: (s) => `/admin/evaluations?suite=${s}` },
    { k: 'reports_pending', icon: FileText, tone: 'warning', to: () => '/admin/reports' },
  ],
  climara: [
    { k: 'active_clients', icon: Building2, tone: 'primary' },
    { k: 'campaigns_active', icon: Radar, tone: 'info', cb: 'campaigns' },
    { k: 'campaigns_expired', icon: AlertTriangle, tone: 'destructive', cb: 'campaigns' },
    { k: 'campaign_requests_pending', icon: Inbox, tone: 'warning', cb: 'setup' },
    { k: 'participation_pct', icon: Users, tone: 'success', suffix: '%' },
    { k: 'campaigns_closed', icon: CheckCircle2, tone: 'muted', cb: 'campaigns' },
  ],
  talenttrain: [
    { k: 'active_clients', icon: Building2, tone: 'primary' },
    { k: 'actions_open', icon: GraduationCap, tone: 'info' },
    { k: 'actions_in_progress', icon: Loader2, tone: 'info' },
    { k: 'actions_completed', icon: CheckCircle2, tone: 'success' },
    { k: 'actions_overdue', icon: AlertTriangle, tone: 'destructive' },
    { k: 'assignments_total', icon: ClipboardList, tone: 'muted', to: (s) => `/admin/evaluations?suite=${s}` },
  ],
  talentcompetency: [
    { k: 'profiles_published', icon: Layers, tone: 'success' },
    { k: 'profiles_zero_linked', icon: Flag, tone: 'warning' },
    { k: 'jp_requests_pending', icon: Inbox, tone: 'warning', to: () => '/admin/talentcompetency/requests' },
    { k: 'pending', icon: Clock, tone: 'info', to: (s) => `/admin/evaluations?suite=${s}` },
    { k: 'submitted', icon: ClipboardList, tone: 'success', to: (s) => `/admin/evaluations?suite=${s}&status=submitted` },
    { k: 'reports_pending', icon: FileText, tone: 'warning', to: () => '/admin/reports' },
  ],
  talentstart: [
    { k: 'active_clients', icon: Building2, tone: 'primary' },
    { k: 'journeys_active', icon: Rocket, tone: 'info' },
    { k: 'journeys_completed', icon: CheckCircle2, tone: 'success' },
    { k: 'journeys_total', icon: ClipboardList, tone: 'muted' },
  ],
  talentheart: [
    { k: 'active_clients', icon: Building2, tone: 'primary' },
    { k: 'recognitions_total', icon: Trophy, tone: 'success' },
    { k: 'surveys_active', icon: HeartPulse, tone: 'info' },
    { k: 'surveys_total', icon: ClipboardList, tone: 'muted' },
    { k: 'th_reports_draft', icon: FileText, tone: 'warning' },
    { k: 'th_reports_released', icon: CheckCircle2, tone: 'success' },
  ],
  talentfix: [
    { k: 'active_clients', icon: Building2, tone: 'primary' },
    { k: 'cases_pending', icon: Inbox, tone: 'warning', cb: 'pending' },
    { k: 'cases_active', icon: Loader2, tone: 'info', cb: 'accepted' },
    { k: 'cases_closed', icon: CheckCircle2, tone: 'muted', cb: 'closed' },
    { k: 'high_severity_open', icon: ShieldAlert, tone: 'destructive' },
    { k: 'oldest_open_days', icon: Hourglass, tone: 'warning', suffixKey: 'days' },
  ],
};

// Which count columns make sense per suite (labels resolved via i18n below).
const CLIENT_COLS = {
  default: ['pending', 'in_progress', 'completed', 'overdue', 'reports_pending'],
  climara: ['active', 'completed', 'overdue', 'pending'],
  talentfix: ['pending', 'active', 'completed', 'overdue'],
  talentheart: ['extra', 'active', 'reports_pending', 'completed'],
  talenttrain: ['pending', 'in_progress', 'completed', 'overdue'],
  talentstart: ['active', 'completed'],
  talentcompetency: ['pending', 'in_progress', 'completed', 'extra', 'reports_pending'],
};

const TONES = {
  primary: 'bg-primary/10 text-primary',
  info: 'bg-info/10 text-info',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
  muted: 'bg-muted text-muted-foreground',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(getUiLocale()); } catch { return String(iso).slice(0, 10); }
};

export const SuiteOpsPanel = ({ suiteCode, onKpiAction, onClientOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advisorFilter, setAdvisorFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    adminApi.suiteConsoleSummary(suiteCode)
      .then((d) => { if (alive) setData(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [suiteCode]);

  const isAdmin = data?.role_view === 'admin';
  const cols = CLIENT_COLS[suiteCode] || CLIENT_COLS.default;
  const colLabel = (f) => t(`suiteOps.cols.${suiteCode}.${f}`, {
    defaultValue: t(`suiteOps.cols.default.${f}`),
  });

  const advisorOptions = useMemo(() => {
    const seen = new Map();
    (data?.by_client || []).forEach((r) => {
      if (r.advisor_id) seen.set(r.advisor_id, r.advisor_name || r.advisor_id);
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [data]);

  const clients = useMemo(() => {
    let list = data?.by_client || [];
    if (isAdmin && advisorFilter !== 'all') list = list.filter((r) => r.advisor_id === advisorFilter);
    if (clientFilter !== 'all') list = list.filter((r) => r.company_id === clientFilter);
    return list;
  }, [data, isAdmin, advisorFilter, clientFilter]);

  const attention = useMemo(() => {
    let list = data?.attention || [];
    if (isAdmin && advisorFilter !== 'all') {
      const names = new Set((data?.by_client || []).filter((r) => r.advisor_id === advisorFilter).map((r) => r.company_id));
      list = list.filter((it) => names.has(it.company_id));
    }
    if (clientFilter !== 'all') list = list.filter((it) => it.company_id === clientFilter);
    return list;
  }, [data, isAdmin, advisorFilter, clientFilter]);

  if (loading) {
    return (
      <Card className="p-6 shadow-card flex items-center gap-2 text-sm text-muted-foreground" data-testid={`suiteops-${suiteCode}-loading`}>
        <Loader2 className="h-4 w-4 animate-spin" /> {t('suiteOps.loading')}
      </Card>
    );
  }
  if (!data) return null;

  const kpis = KPI_SETS[suiteCode] || [];

  const kpiValue = (def) => {
    const v = data.kpis?.[def.k];
    if (v === null || v === undefined) return '—';
    if (def.suffix) return `${v}${def.suffix}`;
    if (def.suffixKey) return `${v} ${t(`suiteOps.${def.suffixKey}`)}`;
    return v;
  };

  const onKpi = (def) => {
    if (def.cb && onKpiAction) { onKpiAction(def.cb); return; }
    if (def.to) navigate(def.to(suiteCode));
  };

  return (
    <div className="space-y-4" data-testid={`suiteops-${suiteCode}`}>
      {/* Executive header line */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight" data-testid={`suiteops-${suiteCode}-title`}>
            {isAdmin ? t('suiteOps.adminTitle') : t('suiteOps.advisorTitle')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {isAdmin
              ? `${t('suiteOps.advisorsActive', { count: data.kpis?.advisors_active ?? 0 })} · ${t('suiteOps.clientsActive', { count: data.kpis?.active_clients ?? 0 })}`
              : t('suiteOps.advisorSubtitle', { clients: data.kpis?.active_clients ?? 0 })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && advisorOptions.length > 0 && (
            <Select value={advisorFilter} onValueChange={setAdvisorFilter}>
              <SelectTrigger className="w-48 h-8 text-xs" data-testid="suiteops-advisor-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('suiteOps.allAdvisors')}</SelectItem>
                {advisorOptions.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {(data.by_client || []).length > 1 && (
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-52 h-8 text-xs" data-testid="suiteops-client-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('suiteOps.allClients')}</SelectItem>
                {(data.by_client || []).map((c) => (
                  <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* KPI layer */}
      <div className={`grid grid-cols-2 md:grid-cols-3 gap-3 ${kpis.length >= 6 ? '2xl:grid-cols-6' : 'xl:grid-cols-4'}`}>
        {kpis.map((def) => {
          const Icon = def.icon;
          const clickable = Boolean(def.to || (def.cb && onKpiAction));
          return (
            <Card
              key={def.k}
              className={`p-4 shadow-card ${clickable ? 'cursor-pointer transition-colors hover:border-primary/40' : ''}`}
              onClick={clickable ? () => onKpi(def) : undefined}
              role={clickable ? 'button' : undefined}
              data-testid={`suiteops-${suiteCode}-kpi-${def.k}`}
            >
              <div className="flex items-center gap-2">
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${TONES[def.tone] || TONES.primary}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <p className="text-xs font-medium text-muted-foreground leading-tight">
                  {t(`suiteOps.kpi.${suiteCode}.${def.k}`, { defaultValue: t(`suiteOps.kpi.default.${def.k}`, { defaultValue: def.k }) })}
                </p>
              </div>
              <p className="text-2xl font-display font-bold mt-2 tabular-nums">{kpiValue(def)}</p>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Workload by advisor (HM Admin) OR My clients (HM Advisor) */}
        {isAdmin ? (
          <Card className="p-0 shadow-card overflow-hidden" data-testid={`suiteops-${suiteCode}-by-advisor`}>
            <div className="px-5 py-4 border-b border-border">
              <h4 className="font-semibold text-sm">{t('suiteOps.byAdvisor')}</h4>
              <p className="text-xs text-muted-foreground">{t('suiteOps.byAdvisorHint')}</p>
            </div>
            {(data.by_advisor || []).length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid={`suiteops-${suiteCode}-by-advisor-empty`}>
                {t('suiteOps.noAdvisorActivity')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                      <th className="py-2 px-4">{t('suiteOps.advisor')}</th>
                      <th className="py-2 px-2 text-center">{t('suiteOps.clients')}</th>
                      <th className="py-2 px-2 text-center">{colLabel('pending')}</th>
                      <th className="py-2 px-2 text-center">{colLabel('active')}</th>
                      <th className="py-2 px-2 text-center">{colLabel('overdue')}</th>
                      <th className="py-2 px-4 text-right">{t('suiteOps.lastActivity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.by_advisor || []).map((g) => (
                      <tr key={g.advisor_id || 'none'} className="border-b last:border-0" data-testid={`suiteops-advisor-row-${g.advisor_id || 'unassigned'}`}>
                        <td className="py-2.5 px-4 font-medium">{g.advisor_name || t('suiteOps.unassigned')}</td>
                        <td className="py-2.5 px-2 text-center tabular-nums">{g.clients}</td>
                        <td className="py-2.5 px-2 text-center tabular-nums">{g.pending}</td>
                        <td className="py-2.5 px-2 text-center tabular-nums">{g.active + (g.in_progress || 0)}</td>
                        <td className="py-2.5 px-2 text-center">
                          {g.overdue > 0
                            ? <Badge className="bg-destructive/10 text-destructive border-destructive/30 tabular-nums">{g.overdue}</Badge>
                            : <span className="tabular-nums text-muted-foreground">0</span>}
                        </td>
                        <td className="py-2.5 px-4 text-right text-xs text-muted-foreground">{fmtDate(g.last_activity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ) : (
          <ClientTable
            title={t('suiteOps.myClients')}
            hint={t('suiteOps.myClientsHint')}
            clients={clients} cols={cols} colLabel={colLabel} t={t}
            suiteCode={suiteCode} onClientOpen={onClientOpen} navigate={navigate}
            showAdvisor={false}
            testid={`suiteops-${suiteCode}-my-clients`}
          />
        )}

        {/* Attention / next actions */}
        <Card className="p-0 shadow-card overflow-hidden" data-testid={`suiteops-${suiteCode}-attention`}>
          <div className="px-5 py-4 border-b border-border">
            <h4 className="font-semibold text-sm">
              {isAdmin ? t('suiteOps.attention') : t('suiteOps.myAttention')}
            </h4>
            <p className="text-xs text-muted-foreground">{t('suiteOps.attentionHint')}</p>
          </div>
          {attention.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid={`suiteops-${suiteCode}-attention-empty`}>
              {t('suiteOps.noAttention')}
            </p>
          ) : (
            <div className="divide-y divide-border max-h-[300px] overflow-y-auto">
              {attention.map((it, i) => (
                <div key={`${it.kind}-${it.id}-${i}`} className="px-5 py-2.5 flex items-center gap-3" data-testid={`suiteops-attention-item-${i}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{it.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {[t(`suiteOps.kind.${it.kind}`), it.company_name, isAdmin ? it.advisor_name : null].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {it.overdue && (
                    <Badge className="bg-destructive/10 text-destructive border-destructive/30 text-xs shrink-0">
                      {t('suiteOps.overdueBadge')}
                    </Badge>
                  )}
                  {it.age_days !== null && it.age_days !== undefined && (
                    <Badge variant="outline" className="text-xs tabular-nums shrink-0">
                      {t('suiteOps.ageDays', { n: it.age_days })}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Activity by client (full width, HM Admin only — advisor already has "My clients") */}
      {isAdmin && (
        <ClientTable
          title={t('suiteOps.byClient')}
          hint={t('suiteOps.byClientHint')}
          clients={clients} cols={cols} colLabel={colLabel} t={t}
          suiteCode={suiteCode} onClientOpen={onClientOpen} navigate={navigate}
          showAdvisor
          testid={`suiteops-${suiteCode}-by-client`}
        />
      )}
    </div>
  );
};

function ClientTable({ title, hint, clients, cols, colLabel, t, suiteCode, onClientOpen, navigate, showAdvisor, testid }) {
  return (
    <Card className="p-0 shadow-card overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border">
        <h4 className="font-semibold text-sm">{title}</h4>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {clients.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
          {t('suiteOps.noClientActivity')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                <th className="py-2 px-4">{t('suiteOps.client')}</th>
                {showAdvisor && <th className="py-2 px-2">{t('suiteOps.advisor')}</th>}
                {cols.map((f) => <th key={f} className="py-2 px-2 text-center">{colLabel(f)}</th>)}
                <th className="py-2 px-4 text-right">{t('suiteOps.lastActivity')}</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((r) => (
                <tr
                  key={r.company_id}
                  className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                  onClick={() => (onClientOpen ? onClientOpen(r.company_id) : navigate(`/admin/clients/${r.company_id}`))}
                  data-testid={`suiteops-client-row-${r.company_id}`}
                >
                  <td className="py-2.5 px-4 font-medium">{r.company_name}</td>
                  {showAdvisor && (
                    <td className="py-2.5 px-2 text-xs text-muted-foreground">{r.advisor_name || t('suiteOps.unassigned')}</td>
                  )}
                  {cols.map((f) => (
                    <td key={f} className="py-2.5 px-2 text-center">
                      {f === 'overdue' && r[f] > 0
                        ? <Badge className="bg-destructive/10 text-destructive border-destructive/30 tabular-nums">{r[f]}</Badge>
                        : <span className={`tabular-nums ${r[f] ? '' : 'text-muted-foreground'}`}>{r[f]}</span>}
                    </td>
                  ))}
                  <td className="py-2.5 px-4 text-right text-xs text-muted-foreground">{fmtDate(r.last_activity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default SuiteOpsPanel;
